import React, { useEffect } from 'react';
import { Flag, ClipboardCheck, CalendarX, Wallet, Clock, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PAYOUT, PAST_WINNERS, SOCIALS } from '../mock';
import { useReveal } from '../hooks/useReveal';

const FIELDS = [
  { key: 'standingsFinal', label: 'When standings become final', Icon: Flag },
  { key: 'howToClaim', label: 'How to claim', Icon: ClipboardCheck },
  { key: 'claimDeadline', label: 'Claim deadline', Icon: CalendarX },
  { key: 'paymentMethod', label: 'Payment method', Icon: Wallet },
  { key: 'paymentTimeframe', label: 'Expected payment time', Icon: Clock },
  { key: 'eligibility', label: 'Eligibility & verification', Icon: ShieldCheck },
];

const fmtUtc = (iso) => new Date(iso).toLocaleString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' UTC';

const OwnerNotice = ({ missing }) => (
  <div data-testid="owner-payout-notice" className="rounded-xl p-4 mb-6 text-sm flex gap-3" style={{ background: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.4)', color: '#fde68a' }}>
    <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
    <span><strong>Owner check:</strong> fill in these payout details in <code>src/mock.js → PAYOUT</code> before publishing: {missing.map((f) => f.label).join(', ')}. Missing items are hidden from visitors.</span>
  </div>
);

function Winners() {
  if (!PAST_WINNERS.length) return null;
  return (
    <div className="ar-panel p-5 sm:p-6 mt-6 reveal" data-testid="past-winners">
      <h3 className="font-display font-bold text-lg mb-4">Previous Winners &amp; Payouts</h3>
      <div className="flex flex-col gap-2">
        {PAST_WINNERS.map((w) => (
          <div key={`${w.month}-${w.place}`} className="grid grid-cols-[1fr_auto] sm:grid-cols-[140px_40px_1fr_80px_110px] gap-x-4 gap-y-1 items-center px-3 py-2.5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.02)' }}>
            <span className="text-muted">{w.month}</span>
            <span className="font-display font-bold text-cyan">#{w.place}</span>
            <span className="font-semibold truncate">{w.username}</span>
            <span className="font-display font-bold text-[#4ADE80]">${w.prize}</span>
            <span className={`text-xs font-bold flex items-center gap-1 ${w.paid ? 'text-[#4ADE80]' : 'text-muted'}`}>{w.paid ? <><CheckCircle2 size={14} aria-hidden="true" /> Paid</> : 'Pending'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Payouts({ meta }) {
  useReveal();
  const missing = FIELDS.filter((f) => !PAYOUT[f.key]);
  const shown = FIELDS.filter((f) => PAYOUT[f.key]);
  const owner = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('owner');
  useEffect(() => { if (missing.length) console.warn('[Owner] Missing payout details:', missing.map((f) => f.key)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section id="payouts" className="py-16 sm:py-24 relative">
      <div className="ar-container max-w-4xl">
        <div className="text-center mb-10 reveal">
          <p className="ar-eyebrow mb-3">Prize Payouts</p>
          <h2 className="ar-heading text-3xl sm:text-4xl md:text-5xl mb-4">How Prizes Are <span className="text-cyan">Paid</span></h2>
          <p className="text-muted max-w-2xl mx-auto">The $500 total monthly prize pool is shared by the top five players when the race closes.</p>
        </div>
        {owner && missing.length > 0 && <OwnerNotice missing={missing} />}
        <div className="ar-panel p-5 sm:p-7 reveal" data-testid="payout-process">
          <ol className="flex flex-col gap-4">
            {meta?.endDate && (
              <li className="flex gap-4" data-testid="payout-race-close">
                <Flag size={20} className="text-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div><p className="font-display font-bold">Race closes</p><p className="text-sm text-muted">The {meta.raceMonth} race closes on {fmtUtc(meta.endDate)}.</p></div>
              </li>
            )}
            {shown.map(({ key, label, Icon }) => (
              <li key={key} className="flex gap-4" data-testid={`payout-${key}`}>
                <Icon size={20} className="text-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div><p className="font-display font-bold">{label}</p><p className="text-sm text-muted">{PAYOUT[key]}</p></div>
              </li>
            ))}
          </ol>
          <p className="text-sm text-muted mt-6 pt-5 border-t border-[var(--ar-border)]">
            Questions about a prize? <a href={SOCIALS.discord} target="_blank" rel="noopener noreferrer" className="text-cyan font-semibold hover:underline" data-testid="payout-discord-link">Contact the team on Discord</a>.
          </p>
        </div>
        <Winners />
      </div>
    </section>
  );
}
